/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { z } from 'zod';

/**
 * Default therapy-programme session types — the starter list seeded into each
 * clinic's admin-managed `sched.therapy_types`. The live list is fetched from
 * the API; this constant is the seed + a client-side fallback when a clinic has
 * none yet. Stored verbatim in `sessions.session_type` (free text).
 */
export const SESSION_TYPES = [
  'ASD Therapy',
  'Speech Therapy',
  'ADHD Therapy',
  'Occupational',
  'Behavioral',
  'Cognitive',
] as const;
export type SessionType = (typeof SESSION_TYPES)[number];

/** A chosen session type is free text (max 80) — validated against the clinic's list in the UI. */
export const sessionTypeSchema = z.string().trim().min(1).max(80);

/**
 * Schedule a session (Figma "Schedule Session"). Mirrors the API
 * `CreateSessionDto`. `scheduledStart` comes from a `datetime-local` input
 * (`YYYY-MM-DDThh:mm`); the client converts it to an ISO instant before submit.
 */
export const CreateSessionSchema = z.object({
  clientId: z.string().uuid('Select a client'),
  therapistId: z.string().uuid().optional(),
  sessionType: sessionTypeSchema.optional(),
  location: z.string().trim().max(120).optional(),
  roomId: z.string().uuid().optional(),
  scheduledStart: z.string().min(1, 'Pick a date and time'),
});
export type CreateSessionInput = z.infer<typeof CreateSessionSchema>;

/**
 * Move a scheduled session to a new time (Figma "Reschedule"). Mirrors the API
 * `RescheduleSessionDto`. `scheduledStart` comes from a `datetime-local` input;
 * the client converts it to an ISO instant before submit.
 */
export const RescheduleSessionSchema = z.object({
  scheduledStart: z.string().min(1, 'Pick a date and time'),
});
export type RescheduleSessionInput = z.infer<typeof RescheduleSessionSchema>;

/**
 * Therapist post-session rating (BRD §4 M02 #4). Engagement and cooperation are
 * scored 1–5; all fields optional so a clinician can rate now and annotate later.
 */
export const RateSessionSchema = z.object({
  engagement: z.number().int().min(1).max(5).optional(),
  cooperation: z.number().int().min(1).max(5).optional(),
  note: z.string().trim().max(1000).optional(),
});
export type RateSessionInput = z.infer<typeof RateSessionSchema>;

/**
 * Reason captured with a session outcome (BRD v2.0 CR-005). Required when marking
 * "consultant attended — client absent"; optional context for "client came late".
 */
export const SessionReasonSchema = z.object({
  reason: z.string().trim().min(1, 'A reason is required').max(500),
});
export type SessionReasonInput = z.infer<typeof SessionReasonSchema>;

/** Minutes in a day — availability windows are stored as minute-of-day (IST). */
const MINUTES_PER_DAY = 24 * 60;

/**
 * One recurring weekly working window for a therapist. `dayOfWeek` is 0 (Sun) …
 * 6 (Sat) in IST; `startMinute` / `endMinute` are minutes from IST-midnight.
 */
export const AvailabilitySlotSchema = z
  .object({
    dayOfWeek: z.number().int().min(0).max(6),
    startMinute: z
      .number()
      .int()
      .min(0)
      .max(MINUTES_PER_DAY - 1),
    endMinute: z.number().int().min(1).max(MINUTES_PER_DAY),
  })
  .refine((slot) => slot.endMinute > slot.startMinute, {
    message: 'End time must be after start time',
    path: ['endMinute'],
  });
export type AvailabilitySlotInput = z.infer<typeof AvailabilitySlotSchema>;

/** Replace a therapist's full weekly availability with this set of windows. */
export const SetAvailabilitySchema = z.object({
  slots: z.array(AvailabilitySlotSchema).max(21),
});
export type SetAvailabilityInput = z.infer<typeof SetAvailabilitySchema>;
